import React, { FC, ReactElement, useContext, useMemo, useState } from 'react'
import { WelcomeContainer } from '../WelcomeContainer/WelcomeContainer'
import { DocumentViewer } from '../DocumentViewer/DocumentViewer'
import { Tags } from '../../../../React/Common/API/Contentful/Constants'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { CookieConsent } from '../../../../../i18n'
import { hasCookieConsent, hasCookieConsentSetting } from '../../../../NonReact/utils/CookieConsentUtils'
import COOKIE_CONSENT from '../../../../../conf/CookiesConsentConfig.json'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { acceptAll, acceptFunctional, rejectAllCookies } from '../../../../NonReact/common/CookieConsent'

export const CookiePolicy: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(CookieConsent.NAME)

    const [_forceUpdate, setForceUpdate]: ReactState<boolean> = useState<boolean>(false)

    const hasAcceptedFunctional: boolean = hasCookieConsent(COOKIE_CONSENT.functional[0])
    const hasAcceptedAll: boolean = hasAcceptedFunctional && hasCookieConsent(COOKIE_CONSENT.statistics[0])

    const message: Nullable<string> = useMemo((): Nullable<string> => {
        if (hasAcceptedAll) {
            return language.getInterpolated(
                CookieConsent.AcceptedAllCookiesMessage,
                language.get(CookieConsent.CookieConsentChangeMessage)
            )
        }

        if (hasAcceptedFunctional) {
            return language.getInterpolated(
                CookieConsent.AcceptedFunctionalCookiesMessage,
                language.get(CookieConsent.CookieConsentChangeMessage)
            )
        }

        if (hasCookieConsentSetting()) {
            return language.getInterpolated(
                CookieConsent.RejectedAllCookiesMessage,
                language.get(CookieConsent.CookieConsentChangeMessage)
            )
        }

        return null
    }, [_forceUpdate, hasAcceptedAll, hasAcceptedFunctional])

    const onClickGenerator =
        (...funcs: (() => void)[]): (() => void) =>
        (): void => {
            if (funcs) {
                funcs.forEach((func: () => void): void => func())
            }

            setForceUpdate((prev: boolean): boolean => !prev)
        }

    return (
        <WelcomeContainer>
            <DocumentViewer
                tag={Tags.COOKIE_POLICY}
                bottomLeft={
                    <div className='cookie-consent'>
                        <div className='ccb-message'>{message}</div>
                    </div>
                }
                bottomRight={
                    <>
                        {hasAcceptedAll || hasAcceptedFunctional || !hasCookieConsentSetting() ? (
                            <RoundedButton
                                onClick={onClickGenerator(
                                    rejectAllCookies(language.get(CookieConsent.RejectionWarning))
                                )}
                            >
                                {hasAcceptedAll || hasAcceptedFunctional
                                    ? language.get(CookieConsent.RevokeAllCookies)
                                    : language.get(CookieConsent.RejectAllCookies)}
                            </RoundedButton>
                        ) : (
                            <></>
                        )}
                        {!hasAcceptedFunctional || hasAcceptedAll ? (
                            <RoundedButton onClick={onClickGenerator(acceptFunctional)}>
                                {hasAcceptedAll
                                    ? language.get(CookieConsent.RevokeStatisticsCookies)
                                    : language.get(CookieConsent.AcceptFunctionalityCookies)}
                            </RoundedButton>
                        ) : (
                            <></>
                        )}
                        {!hasAcceptedAll ? (
                            <RoundedButton onClick={onClickGenerator(acceptAll)}>
                                {language.get(CookieConsent.AcceptAllCookies)}
                            </RoundedButton>
                        ) : (
                            <></>
                        )}
                    </>
                }
            />
        </WelcomeContainer>
    )
}
